import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Form,
  Input,
  Button,
  Icon,
  Modal,
  Checkbox,
  Row,
  Col,
  Typography,
  List,
} from "antd";
import PasswordValidTick, { validatePassword } from "./PasswordValidTick";
import PasswordMatchTick from "./PasswordMatchTick";
import { useIsDesktop } from "../../../hooks";
import { Tooltip } from "@authzsvc/common-react-components";

const { Text } = Typography;

const ChangePasswordModal = ({
  identity,
  onSubmit,
  title,
  loading,
  disabledOptions: { passwordChangeDisabled = false, message = "" } = {},
}) => {
  const [visible, setVisible] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState(null);
  const [oldPasswordError, setOldPasswordError] = useState(null);
  const [activePolicy, setActivePolicy] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmRules, setConfirmRules] = useState(false);
  const isDesktop = useIsDesktop();

  const close = () => {
    setNewPassword("");
    setConfirmNewPassword("");
    setCurrentPassword("");
    setConfirmRules(false);
    setNewPasswordError(null);
    setOldPasswordError(null);
    setVisible(false);
  };

  const isValidPassword = () => {
    return (
      newPassword !== confirmNewPassword ||
      !newPassword ||
      !confirmRules ||
      !confirmNewPassword ||
      !currentPassword ||
      loading ||
      !validatePassword(newPassword)
    );
  };

  const submit = async () => {
    const invalid = isValidPassword();
    if (!invalid) {
      const resp = await onSubmit(currentPassword, newPassword);
      if (passwordSubmissionErrors(resp)) {
        close();
      }
    } else {
      setNewPasswordError("Please fill all fields and confirm rules");
    }
  };

  const passwordSubmissionErrors = (value) => {
    if (value.payload.data?.error) {
      const err = value.payload.data.error;
      if (err.includes("old password")) {
        setOldPasswordError(err);
      }
      if (err.includes("new password")) {
        setNewPasswordError(err);
      }
      return false;
    }

    if (value.payload.data?.errors) {
      const errs = value.payload.data.errors;
      if (errs.compromised_password) {
        setNewPasswordError(errs.compromised_password);
      }
      if (errs.ActiveDirectoryManager) {
        if (errs.ActiveDirectoryManager.includes("new password")) {
          setNewPasswordError(errs.ActiveDirectoryManager);
        } else if (errs.ActiveDirectoryManager.includes("old password")) {
          setOldPasswordError(errs.ActiveDirectoryManager);
        }
      }
      if (errs.display_name_found) {
        setNewPasswordError(errs.display_name_found);
      }
      if (errs.username_found) {
        setNewPasswordError(errs.username_found);
      }
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewPasswordError(null);
    setOldPasswordError(null);
    if (name === "newPassword") {
      setNewPassword(value);
      setActivePolicy("8-chars");
    } else if (name === "confirmNewPassword") {
      setConfirmNewPassword(value);
    } else if (name === "currentPassword") {
      setCurrentPassword(value);
    }
  };

  const RestrictionsMessage = () => (
    <div className="password-policy-margin-top">
      <Text strong>Password requirements:</Text>
      <List
        size="small"
        dataSource={[
          {
            key: "8-chars",
            text: "At least 8 characters long, with character restrictions.",
          },
        ]}
        renderItem={(item) => (
          <List.Item>
            <Icon
              type="right-circle"
              theme={activePolicy === item.key ? "filled" : "outlined"}
              className={activePolicy === item.key ? "active-policy" : ""}
              style={{ marginRight: 8 }}
            />
            <Text>{item.text}</Text>
          </List.Item>
        )}
      />
    </div>
  );

  return (
    <>
      <Tooltip
        title={message}
        position="left center"
        disabled={!passwordChangeDisabled}
      >
        <Button
          icon="login"
          type="primary"
          block
          disabled={passwordChangeDisabled}
          size="large"
          onClick={() => {
            if (!passwordChangeDisabled) {
              setVisible(true);
            }
          }}
        >
          {title}
        </Button>
      </Tooltip>

      <Modal
        centered
        visible={visible}
        title={
          <span>
            <Icon type="redo" spin={loading} />{" "}
            <Text strong>{title} for account: </Text>
            <Text code>{identity.upn}</Text>
          </span>
        }
        width={700}
        onCancel={close}
        footer={[
          <Button
            key="cancel"
            onClick={close}
            loading={loading}
            disabled={loading}
            type="danger"
            icon="close"
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon="check"
            onClick={submit}
            loading={loading}
            disabled={isValidPassword()}
          >
            Confirm
          </Button>,
        ]}
        maskClosable
      >
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Current password"
                validateStatus={oldPasswordError ? "error" : ""}
                help={oldPasswordError}
              >
                <Input.Password
                  name="currentPassword"
                  id="changeCurrentPasswordField"
                  value={currentPassword}
                  onChange={handleChange}
                  autoFocus
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={isDesktop ? 12 : 24}>
              <Form.Item
                label="New password"
                validateStatus={newPasswordError ? "error" : ""}
                help={newPasswordError}
              >
                <Input.Password
                  name="newPassword"
                  id="changeNewPasswordField"
                  value={newPassword}
                  onChange={handleChange}
                />
              </Form.Item>

              <Form.Item label="Confirm new password">
                <Input.Password
                  name="confirmNewPassword"
                  id="changeConfirmNewPasswordField"
                  value={confirmNewPassword}
                  onChange={handleChange}
                />
              </Form.Item>

              {!isDesktop && <RestrictionsMessage />}
            </Col>

            {isDesktop && (
              <Col md={12} className="policies">
                <RestrictionsMessage />
              </Col>
            )}
          </Row>

          <PasswordValidTick
            newPassword={newPassword}
            setNewPasswordError={setNewPasswordError}
          />
          <PasswordMatchTick
            newPassword={newPassword}
            confirmNewPassword={confirmNewPassword}
          />

          <Form.Item className="password-oc5-margin">
            <Checkbox
              id="checkbox"
              checked={confirmRules}
              onChange={() => setConfirmRules(!confirmRules)}
            >
              I confirm that I have read, understood and will abide by the{" "}
              <a
                href="https://security.web.cern.ch/rules/en/index.shtml"
                target="_blank"
                rel="noopener noreferrer"
              >
                CERN Computing Rules (OC5)
              </a>{" "}
              *
            </Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

ChangePasswordModal.propTypes = {
  loading: PropTypes.bool.isRequired,
  identity: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  disabledOptions: PropTypes.shape({
    disabled: PropTypes.bool,
    message: PropTypes.string,
  }),
};

export default ChangePasswordModal;
